import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ArrowRight, ChevronDown, ExternalLink, Layers, Database, FileBadge, Fingerprint, Building2, Wrench, Store, BookOpen, FileText, Users } from "lucide-react";
import { TRexWordmark } from "./TRexWordmark";
import { LanguageSwitcher } from "./LanguageSwitcher";

/**
 * Mega menu content. Per section (lining up with `navItems` below) we provide
 * an editorial title + tagline (left column) and rich child cards (right column).
 *
 * NOTE: titles/descriptions are copy-driven by the brand voice. When the FR/ES
 * rollout lands, move these strings into the i18n bundles.
 */
const MEGA = {
  network: {
    title: "The utility network",
    tagline: "One compliance-aware book of record for tokenised assets. Open, neutral, compliant.",
    items: [
      { key: "overview", href: "/network", title: "Overview", desc: "Why the network exists, who runs it, what it settles.", icon: Layers },
      { key: "ledger", href: "/network/ledger", title: "T-REX Ledger", desc: "The canonical reference ledger for regulated assets.", icon: Database },
      { key: "erc", href: "/network/erc-3643", title: "ERC-3643", desc: "The open standard powering permissioned digital assets.", icon: FileBadge },
    ],
  },
  solutions: {
    title: "Solutions",
    tagline: "Purpose-built for the institutions, builders and infrastructure providers bringing capital onchain.",
    items: [
      { key: "institutions", href: "/solutions/institutions", title: "For Institutions", desc: "Compliance, identity & orchestration for regulated issuers.", icon: Building2 },
      { key: "builders", href: "/solutions/builders", title: "For Builders", desc: "Ship apps on the canonical compliance layer and reach institutional flows.", icon: Wrench },
      { key: "appStore", href: "/solutions/app-store", title: "App Store", desc: "A vetted ecosystem of applications, accessible through one interface.", icon: Store },
    ],
  },
  resources: {
    title: "Resources",
    tagline: "Documentation, editorial coverage, and the people behind the standard.",
    items: [
      { key: "articles", href: "/articles", title: "Articles", desc: "Editorial coverage of the regulated tokenisation thesis.", icon: FileText },
      { key: "docs", href: "https://docs.t-rex.network/", external: true, title: "Docs", desc: "Technical reference, integration guides, and protocol specs.", icon: BookOpen },
      { key: "about", href: "/about", title: "About", desc: "Eight years in production. The team behind ERC-3643.", icon: Users },
    ],
  },
};

const navItems = [
  { key: "network", type: "dropdown" },
  { key: "solutions", type: "dropdown" },
  { key: "ecosystem", type: "link", href: "/ecosystem" },
  { key: "learn", type: "link", href: "/learn" },
  { key: "resources", type: "dropdown" },
];

const NAVBAR_HEIGHT = 60; // CSS px — kept in sync with `top-[60px]` on AnchorNav and DocsLayout

export function Navbar() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpenSection, setMobileOpenSection] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const closeTimeout = useRef(null);

  const isActiveItem = useCallback((item) => {
    if (item.type === "link") return location.pathname === item.href;
    const mega = MEGA[item.key];
    return !!mega && mega.items.some((c) => !c.external && location.pathname.startsWith(c.href));
  }, [location.pathname]);

  const isChildActive = useCallback((href) => location.pathname === href, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileOpenSection(null);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Body scroll lock while the mobile drawer is open. Pure `overflow:hidden`
  // is not enough on mobile (especially iOS Safari): the page still scrolls
  // programmatically and via touch. We use the position:fixed + negative-top
  // pattern and restore the prior scroll position on close.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [mobileOpen]);

  // Esc closes the mega menu
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpenDropdown(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const hoverOpen = (key) => {
    if (closeTimeout.current) { clearTimeout(closeTimeout.current); closeTimeout.current = null; }
    setOpenDropdown(key);
  };
  const hoverClose = () => {
    closeTimeout.current = setTimeout(() => setOpenDropdown(null), 140);
  };
  const cancelClose = () => {
    if (closeTimeout.current) { clearTimeout(closeTimeout.current); closeTimeout.current = null; }
  };

  const activeMega = openDropdown && MEGA[openDropdown];

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="site-navbar">
      <div
        className={`border-b transition-colors duration-200 bg-aubergine ${
          // When the mega menu is open, remove the hairline so the panel feels welded to the navbar
          openDropdown ? "border-transparent" : scrolled ? "border-champ-light/10 shadow-[0_1px_0_rgba(0,0,0,0.4)]" : "border-transparent"
        }`}
        onMouseLeave={hoverClose}
      >
        <nav className="mx-auto flex h-[60px] max-w-7xl items-center justify-between px-6 lg:px-8">
          <Link to="/" className="flex items-center group" data-testid="navbar-logo" aria-label="T-REX Network">
            <TRexWordmark className="h-7 w-auto transition-opacity group-hover:opacity-85" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex md:items-center md:gap-1">
            {navItems.map((item) => {
              const active = isActiveItem(item);
              if (item.type === "link") {
                return (
                  <Link
                    key={item.key}
                    to={item.href}
                    data-testid={`nav-link-${item.key}`}
                    onMouseEnter={hoverClose}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors rounded-md ${
                      active ? "text-bone bg-aubergine-tier" : "text-bone/70 hover:text-bone hover:bg-aubergine-tier/60"
                    }`}
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                );
              }
              const open = openDropdown === item.key;
              return (
                <div
                  key={item.key}
                  className="relative"
                  onMouseEnter={() => hoverOpen(item.key)}
                >
                  <button
                    type="button"
                    onClick={() => setOpenDropdown(open ? null : item.key)}
                    data-testid={`nav-dropdown-${item.key}`}
                    aria-expanded={open}
                    aria-haspopup="true"
                    className={`relative inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium transition-colors rounded-md ${
                      open || active
                        ? "text-bone bg-aubergine-tier"
                        : "text-bone/70 hover:text-bone hover:bg-aubergine-tier/60"
                    }`}
                  >
                    {t(`nav.${item.key}`)}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
                    {/* Champagne underline when the panel is open — feels welded */}
                    {open && (
                      <span
                        aria-hidden="true"
                        className="absolute left-3 right-3 -bottom-[14px] h-px bg-champagne"
                      />
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="hidden md:flex md:items-center md:gap-2">
            {/* TODO(i18n): Re-enable LanguageSwitcher once FR/ES content is ready.
                <LanguageSwitcher variant="dark" /> */}
            <Link
              to="/contact"
              data-testid="navbar-cta-contact"
              onMouseEnter={hoverClose}
              className="inline-flex items-center justify-center rounded-md bg-champagne text-aubergine px-4 py-2 font-mono text-xs font-medium tracking-[0.04em] transition-all hover:bg-champ-light hover:shadow-[0_0_0_4px_rgba(168,139,74,0.15)]"
            >
              {t("nav.contact")}
              <ArrowRight className="ml-1.5 h-3 w-3" />
            </Link>
          </div>

          <button
            type="button"
            className="md:hidden rounded-md p-2 text-bone/70 hover:text-bone hover:bg-aubergine-tier/60 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>

        {/* MEGA MENU PANEL — full-width, flush with the navbar.
            Light bone surface for legibility — feels like the navbar "opens"
            a drawer below itself. The champagne hairline at top welds the two. */}
        {activeMega && (
          <div
            className="hidden md:block absolute left-0 right-0 top-full bg-bone border-t border-champagne/50 border-b border-aubergine/10 shadow-[0_24px_64px_-32px_rgba(21,16,42,0.35)] animate-[fade-down_180ms_cubic-bezier(.2,.6,.2,1)_both]"
            data-testid={`nav-megamenu-${openDropdown}`}
            onMouseEnter={cancelClose}
            onMouseLeave={hoverClose}
          >
            <div className="mx-auto max-w-7xl px-6 md:px-8 py-10">
              <div className="grid grid-cols-12 gap-10">
                {/* Left rail — section identity */}
                <div className="col-span-12 md:col-span-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-champagne mb-3">
                    {t(`nav.${openDropdown}`)}
                  </p>
                  <h3
                    className="text-2xl xl:text-3xl text-aubergine leading-[1.15] tracking-tight"
                    style={{ fontFamily: "var(--font-display)", fontWeight: 400 }}
                  >
                    {activeMega.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-aubergine/65 max-w-sm">
                    {activeMega.tagline}
                  </p>
                </div>

                {/* Right — page cards */}
                <div className="col-span-12 md:col-span-8">
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {activeMega.items.map((c) => {
                      const Icon = c.icon;
                      const childActive = !c.external && isChildActive(c.href);
                      const baseRow = `group flex items-start gap-3 rounded-md px-4 py-3.5 transition-colors border ${
                        childActive
                          ? "border-champagne/50 bg-champagne/10"
                          : "border-transparent hover:border-aubergine/10 hover:bg-bone-soft"
                      }`;
                      const content = (
                        <>
                          <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-md bg-aubergine/5 text-champagne flex-shrink-0 ring-1 ring-aubergine/8">
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2">
                              <span className="text-[15px] font-medium text-aubergine">{c.title}</span>
                              {c.external
                                ? <ExternalLink className="h-3 w-3 text-aubergine/45" />
                                : <ArrowRight className="h-3.5 w-3.5 text-aubergine/35 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-champagne" />}
                            </span>
                            <span className="block mt-1 text-xs leading-relaxed text-aubergine/65">
                              {c.desc}
                            </span>
                          </span>
                        </>
                      );
                      if (c.external) {
                        return (
                          <li key={c.key}>
                            <a
                              href={c.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`nav-dropdown-item-${c.key}`}
                              className={baseRow}
                            >
                              {content}
                            </a>
                          </li>
                        );
                      }
                      return (
                        <li key={c.key}>
                          <Link
                            to={c.href}
                            data-testid={`nav-dropdown-item-${c.key}`}
                            className={baseRow}
                            onClick={() => setOpenDropdown(null)}
                          >
                            {content}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="md:hidden border-b border-champ-light/10 bg-aubergine max-h-[calc(100vh-60px)] overflow-y-auto overscroll-contain"
          data-testid="navbar-mobile-menu"
        >
          <div className="px-6 py-4 space-y-1">
            {navItems.map((item) => {
              if (item.type === "link") {
                return (
                  <Link
                    key={item.key}
                    to={item.href}
                    data-testid={`nav-mobile-link-${item.key}`}
                    className={`block py-3 px-3 text-base font-medium rounded-md ${
                      location.pathname === item.href ? "text-bone bg-aubergine-tier" : "text-bone/80 hover:text-bone hover:bg-aubergine-tier/60"
                    }`}
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                );
              }
              const open = mobileOpenSection === item.key;
              const mega = MEGA[item.key];
              return (
                <div key={item.key}>
                  <button
                    type="button"
                    onClick={() => setMobileOpenSection(open ? null : item.key)}
                    data-testid={`nav-mobile-section-${item.key}`}
                    className="w-full flex items-center justify-between py-3 px-3 text-base font-medium rounded-md text-bone/80 hover:text-bone hover:bg-aubergine-tier/60"
                  >
                    {t(`nav.${item.key}`)}
                    <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && mega && (
                    <div className="pl-3 mt-1 border-l border-champ-light/15 ml-3 space-y-0.5">
                      {mega.items.map((c) => {
                        if (c.external) {
                          return (
                            <a
                              key={c.key}
                              href={c.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`nav-mobile-item-${c.key}`}
                              className="flex items-center justify-between py-2.5 px-3 text-sm text-bone/70 hover:text-bone hover:bg-aubergine-tier/60 rounded-md"
                            >
                              {c.title}
                              <ExternalLink className="h-3 w-3 text-bone/45" />
                            </a>
                          );
                        }
                        return (
                          <Link
                            key={c.key}
                            to={c.href}
                            data-testid={`nav-mobile-item-${c.key}`}
                            className={`block py-2.5 px-3 text-sm rounded-md ${
                              location.pathname === c.href ? "text-bone bg-aubergine-tier" : "text-bone/70 hover:text-bone hover:bg-aubergine-tier/60"
                            }`}
                          >
                            {c.title}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            <Link
              to="/contact"
              data-testid="navbar-mobile-cta-contact"
              className="mt-4 flex w-full items-center justify-center rounded-md bg-champagne text-aubergine py-3 font-mono text-xs font-medium tracking-[0.04em] hover:bg-champ-light"
            >
              {t("nav.contact")}
              <ArrowRight className="ml-1.5 h-3 w-3" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

const __NAVBAR_HEIGHT_REF = NAVBAR_HEIGHT;
export { __NAVBAR_HEIGHT_REF };
